import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import MerchantLogo from "../../components/AppBar/MerchantLogo";
import {
    Button,
    ButtonExternalLink,
    ButtonGroup,
} from "../../components/Buttons";
import Icon from "../../components/Icons";
import Modal from "../../components/Modal";
import { P } from "../../components/Typography";
import View from "../../components/View";
import { ONBOARDING_HOSTNAME } from "../../env";
import { distances, palette, zIndex } from "../../styles/constants";
import { AuthenticatedAccountUser } from "../../types/management-auth";
import AccountList from "./AccountList";
import { GhostLink } from "./GhostLink";
import Portal from "./Portal";

type AccountSwitcherProps = {
    currentAccount?: AuthenticatedAccountUser["accounts"][number] | undefined;
    onChangeAccount: (
        account: AuthenticatedAccountUser["accounts"][number],
    ) => void;
    onCreateAccountClick?: () => void;
};

export default function AccountSwitcher({
    currentAccount,
    onChangeAccount,
    onCreateAccountClick,
}: AccountSwitcherProps) {
    return (
        <s.Wrapper>
            <CurrentAccountHero account={currentAccount} />
            <AccountList
                currentAccount={currentAccount}
                onChangeAccount={onChangeAccount}
            />
            <CreateAccountActions onCreateAccountClick={onCreateAccountClick} />
        </s.Wrapper>
    );
}

type CurrentAccountHeroProps = {
    account: AuthenticatedAccountUser["accounts"][number] | undefined;
};

function CurrentAccountHero({ account }: CurrentAccountHeroProps) {
    if (!account) {
        return null;
    }
    return (
        <s.Hero>
            <s.LogoHeroImageWrapper>
                <MerchantLogo
                    src={account.icon_url}
                    alt={
                        account.display_name ||
                        account.business_name ||
                        account.account_id ||
                        ""
                    }
                    size="md"
                />
            </s.LogoHeroImageWrapper>
            <GhostLink copy={account.display_name || account.business_name}>
                <P variant="md" fontWeight={600} color="inherit">
                    {account.display_name || account.business_name}
                </P>
            </GhostLink>
            <GhostLink copy={account.organization_number}>
                <Trans
                    i18nKey="change_account.org_no"
                    ns="app"
                    values={{ orgNo: account.organization_number }}
                    parent={P}
                    color={palette.neutral[300]}
                >
                    <P
                        color={palette.neutral[300]}
                        fontWeight={500}
                        as="strong"
                    >
                        {account.organization_number}
                    </P>
                </Trans>
            </GhostLink>
            <GhostLink copy={account.account_id}>
                <Trans
                    i18nKey="change_account.dintero_acc_no"
                    ns="app"
                    values={{ accountId: account.account_id }}
                    parent={P}
                    color={palette.neutral[300]}
                >
                    <P
                        color={palette.neutral[300]}
                        fontWeight={500}
                        as="strong"
                    >
                        {account.account_id}
                    </P>
                </Trans>
            </GhostLink>
        </s.Hero>
    );
}

type CreateAccountActionsProps = {
    onCreateAccountClick?: () => void;
};

function CreateAccountActions({
    onCreateAccountClick,
}: CreateAccountActionsProps) {
    const { t } = useTranslation();
    const [opened, setOpen] = useState(false);
    return (
        <s.BottomPadding>
            <Button
                onClick={() => {
                    setOpen(true);
                    if (onCreateAccountClick) {
                        onCreateAccountClick();
                    }
                }}
                className="text white has-icons"
            >
                <Icon icon="plus" />
                {t("change_account.add_account")}
            </Button>
            {opened && (
                <Portal>
                    <Modal
                        width="5ch"
                        title={t("change_account.add_account")}
                        centeredHeader
                        backdropStyle={{
                            zIndex: zIndex.mount_everest,
                        }}
                    >
                        <View
                            direction="column"
                            gap={distances.small}
                            pt={distances.small}
                        >
                            <Trans
                                i18nKey="change_account.add_account_description"
                                ns="app"
                                parent={P}
                                textAlign="center"
                            >
                                <P as="strong" />
                            </Trans>
                            <ButtonGroup justify="center">
                                <Button
                                    className="default"
                                    onClick={() => setOpen(false)}
                                >
                                    {t("change_account.back")}
                                </Button>
                                <ButtonExternalLink
                                    href={ONBOARDING_HOSTNAME}
                                    target="_blank"
                                    onClick={() => setOpen(false)}
                                >
                                    {t("change_account.create_account")}
                                </ButtonExternalLink>
                            </ButtonGroup>
                        </View>
                    </Modal>
                </Portal>
            )}
        </s.BottomPadding>
    );
}

const s = {
    Wrapper: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    height: 100%;
    width: 100%;
    color: ${palette.white};
  `,
    Hero: styled.div`
      display: flex;
      text-align: center;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      color: inherit;
      padding: ${distances.tiny};
      padding-block: ${distances.small};
      .ghost-link-indicator>svg {
        color: ${palette.neutral[400]};
      }
  `,
    LogoHeroImageWrapper: styled.div`
        margin-bottom: ${distances.tiny};
  `,
    BottomPadding: styled.div`
      padding: ${distances.tiny};
      margin-bottom: ${distances.tiny};
      `,
};
